/**
 * External dependencies
 */
import { stringify } from '@nab/utils';
import type { Maybe } from '@nab/types';
import type { CSSProperties } from 'react';

/**
 * WordPress dependencies
 */
import {
	hasSplitBorders,
	ToolsPanel,
	ToolsPanelItem,
	ItemGroup,
	BaseControl,
} from '@safe-wordpress/components';
import { _x } from '@safe-wordpress/i18n';

/**
 * Internal dependencies
 */
import {
	BorderBoxControl,
	BorderRadiusControl,
	ShadowPopover,
	useShadowPresets,
	getEditorSettings,
	useColorsPerOrigin,
	type FullEditorSettings,
	type Border,
	type Borders,
} from './internal';
import {
	getInheritedValue,
	getPreviewValue,
	getUserValue,
	hasInheritedValue,
	hasUserValue,
} from '../../../../utils/props';
import type { ToolsPanelProps } from './types';

export function useHasBorderPanel( settings: FullEditorSettings ): boolean {
	const controls = Object.values( useHasBorderPanelControls( settings ) );
	return controls.some( Boolean );
}

export function useBorderPanelLabel( {
	hasBorderControl,
	hasShadowControl,
}: {
	readonly hasBorderControl: boolean;
	readonly hasShadowControl: boolean;
} ): string {
	const settings = getEditorSettings();
	const controls = useHasBorderPanelControls( settings );

	if ( ! hasBorderControl && ! hasShadowControl ) {
		hasBorderControl =
			controls?.hasBorderColor ||
			controls?.hasBorderStyle ||
			controls?.hasBorderWidth ||
			controls?.hasBorderRadius;
		hasShadowControl = controls?.hasShadow;
	}

	if ( hasBorderControl && hasShadowControl ) {
		return _x( 'Border & Shadow', 'text', 'nelio-ab-testing' );
	}

	if ( hasShadowControl ) {
		return _x( 'Shadow', 'text', 'nelio-ab-testing' );
	}

	return _x( 'Border', 'text', 'nelio-ab-testing' );
}

export function useHasBorderPanelControls( settings: FullEditorSettings ): {
	hasBorderColor: boolean;
	hasBorderRadius: boolean;
	hasBorderStyle: boolean;
	hasBorderWidth: boolean;
	hasShadow: boolean;
} {
	const controls = {
		hasBorderColor: useHasBorderColorControl( settings ),
		hasBorderRadius: useHasBorderRadiusControl( settings ),
		hasBorderStyle: useHasBorderStyleControl( settings ),
		hasBorderWidth: useHasBorderWidthControl( settings ),
		hasShadow: useHasShadowControl( settings ),
	};

	return controls;
}

function useHasBorderColorControl( settings: FullEditorSettings ) {
	return settings?.border?.color;
}

function useHasBorderRadiusControl( settings: FullEditorSettings ) {
	return settings?.border?.radius;
}

function useHasBorderStyleControl( settings: FullEditorSettings ) {
	return settings?.border?.style;
}

function useHasBorderWidthControl( settings: FullEditorSettings ) {
	return settings?.border?.width;
}

function useHasShadowControl( settings: FullEditorSettings ) {
	const shadows = useShadowPresets( settings );
	return !! settings?.shadow && shadows.length > 0;
}

type BorderValue = {
	readonly border?: Border & {
		top?: Border;
		right?: Border;
		bottom?: Border;
		left?: Border;
		radius?:
			| {
					readonly flat?: string;
					readonly topLeft?: string;
					readonly topRight?: string;
					readonly bottomLeft?: string;
					readonly bottomRight?: string;
			  }
			| string;
	};
	readonly shadow?: string;
};

export function BorderPanel( {
	value,
	onChange,
}: ToolsPanelProps ): JSX.Element {
	const settings = getEditorSettings();
	const colors = useColorsPerOrigin( settings );

	const encodeColorValue = ( colorValue: CSSProperties[ 'borderColor' ] ) => {
		const allColors = colors.flatMap(
			( { colors: originColors } ) => originColors
		);
		const colorObject = allColors.find(
			( { color } ) => color === colorValue
		);
		return colorObject
			? 'var:preset|color|' + colorObject.slug
			: colorValue;
	};
	const border = useBorder( value );
	const readonlyBorders = useBorderReadonlies( value );
	const hasBorderValue = () =>
		hasUserValue( value.borderTopColor ) ||
		hasUserValue( value.borderTopStyle ) ||
		hasUserValue( value.borderTopWidth ) ||
		hasUserValue( value.borderRightColor ) ||
		hasUserValue( value.borderRightStyle ) ||
		hasUserValue( value.borderRightWidth ) ||
		hasUserValue( value.borderBottomColor ) ||
		hasUserValue( value.borderBottomStyle ) ||
		hasUserValue( value.borderBottomWidth ) ||
		hasUserValue( value.borderLeftColor ) ||
		hasUserValue( value.borderLeftStyle ) ||
		hasUserValue( value.borderLeftWidth );
	const isBorderShownByDefault =
		hasInheritedValue( value.borderTopColor ) ||
		hasInheritedValue( value.borderTopStyle ) ||
		hasInheritedValue( value.borderTopWidth ) ||
		hasInheritedValue( value.borderRightColor ) ||
		hasInheritedValue( value.borderRightStyle ) ||
		hasInheritedValue( value.borderRightWidth ) ||
		hasInheritedValue( value.borderBottomColor ) ||
		hasInheritedValue( value.borderBottomStyle ) ||
		hasInheritedValue( value.borderBottomWidth ) ||
		hasInheritedValue( value.borderLeftColor ) ||
		hasInheritedValue( value.borderLeftStyle ) ||
		hasInheritedValue( value.borderLeftWidth );
	const setBorder = ( newBorder: BorderValue[ 'border' ] ) =>
		onChange( {
			borderTopColor: getColorFromPreset(
				newBorder?.color ?? newBorder?.top?.color
			),
			borderTopStyle: newBorder?.style ?? newBorder?.top?.style,
			borderTopWidth: stringify(
				newBorder?.width ?? newBorder?.top?.width
			),
			borderRightColor: getColorFromPreset(
				newBorder?.color ?? newBorder?.right?.color
			),
			borderRightStyle: newBorder?.style ?? newBorder?.right?.style,
			borderRightWidth: stringify(
				newBorder?.width ?? newBorder?.right?.width
			),
			borderBottomColor: getColorFromPreset(
				newBorder?.color ?? newBorder?.bottom?.color
			),
			borderBottomStyle: newBorder?.style ?? newBorder?.bottom?.style,
			borderBottomWidth: stringify(
				newBorder?.width ?? newBorder?.bottom?.width
			),
			borderLeftColor: getColorFromPreset(
				newBorder?.color ?? newBorder?.left?.color
			),
			borderLeftStyle: newBorder?.style ?? newBorder?.left?.style,
			borderLeftWidth: stringify(
				newBorder?.width ?? newBorder?.left?.width
			),
		} );
	const showBorderColor = useHasBorderColorControl( settings );
	const showBorderStyle = useHasBorderStyleControl( settings );
	const showBorderWidth = useHasBorderWidthControl( settings );

	// Border radius.
	const showBorderRadius = useHasBorderRadiusControl( settings );
	const borderRadiusValues = border?.radius;
	const setBorderRadius = (
		newBorderRadius: Exclude<
			BorderValue[ 'border' ],
			undefined
		>[ 'radius' ]
	) =>
		onChange(
			'string' === typeof newBorderRadius
				? {
						borderTopLeftRadius: newBorderRadius,
						borderTopRightRadius: newBorderRadius,
						borderBottomRightRadius: newBorderRadius,
						borderBottomLeftRadius: newBorderRadius,
				  }
				: {
						borderTopLeftRadius: newBorderRadius?.topLeft,
						borderTopRightRadius: newBorderRadius?.topRight,
						borderBottomRightRadius: newBorderRadius?.bottomRight,
						borderBottomLeftRadius: newBorderRadius?.bottomLeft,
				  }
		);
	const hasBorderRadius = () =>
		hasUserValue( value.borderTopLeftRadius ) ||
		hasUserValue( value.borderTopRightRadius ) ||
		hasUserValue( value.borderBottomRightRadius ) ||
		hasUserValue( value.borderBottomLeftRadius );
	const isBorderRadiusShownByDefault =
		hasInheritedValue( value.borderTopLeftRadius ) ||
		hasInheritedValue( value.borderTopRightRadius ) ||
		hasInheritedValue( value.borderBottomRightRadius ) ||
		hasInheritedValue( value.borderBottomLeftRadius );
	const hasShadowControl = useHasShadowControl( settings );

	const resetBorder = () => setBorder( undefined );
	const onBorderChange = ( newBorder: BorderValue[ 'border' ] ) => {
		// Ensure we have a visible border style when a border width or
		// color is being selected.
		const updatedBorder: BorderValue[ 'border' ] = { ...newBorder };

		if ( hasSplitBorders( updatedBorder ) ) {
			(
				[ 'top', 'right', 'bottom', 'left' ] as Array< keyof Borders >
			 ).forEach( ( side ) => {
				if ( updatedBorder[ side ] ) {
					updatedBorder[ side ] = {
						...updatedBorder[ side ],
						color: encodeColorValue( updatedBorder[ side ]?.color ),
					};
				}
			} );
		} else if ( updatedBorder ) {
			updatedBorder.color = encodeColorValue( updatedBorder.color );
		}

		// As radius is maintained separately to color, style, and width
		// maintain its value. Undefined values here will be cleaned when
		// global styles are saved.
		setBorder( { radius: border?.radius, ...updatedBorder } );
	};

	const hasBorderControl =
		showBorderColor ||
		showBorderStyle ||
		showBorderWidth ||
		showBorderRadius;

	const label = useBorderPanelLabel( {
		hasShadowControl,
		hasBorderControl,
	} );

	const resetAll = () =>
		onChange( {
			border: undefined,
			borderRadius: undefined,
			boxShadow: undefined,
		} );

	return (
		<ToolsPanel label={ label } resetAll={ resetAll }>
			{ ( showBorderWidth || showBorderColor ) && (
				<ToolsPanelItem
					hasValue={ hasBorderValue }
					label={ _x( 'Border', 'text', 'nelio-ab-testing' ) }
					onDeselect={ () => resetBorder() }
					isShownByDefault={ isBorderShownByDefault }
				>
					<BorderBoxControl
						colors={ colors }
						enableAlpha
						enableStyle={ showBorderStyle }
						onChange={ onBorderChange }
						popoverOffset={ 40 }
						popoverPlacement="left-start"
						value={ border }
						__experimentalIsRenderedInSidebar
						size="__unstable-large"
						hideLabelFromVision={ ! hasShadowControl }
						label={ _x( 'Border', 'text', 'nelio-ab-testing' ) }
						readonlyBorders={ readonlyBorders }
					/>
				</ToolsPanelItem>
			) }
			{ showBorderRadius && (
				<ToolsPanelItem
					hasValue={ hasBorderRadius }
					label={ _x( 'Radius', 'text', 'nelio-ab-testing' ) }
					onDeselect={ () => setBorderRadius( undefined ) }
					isShownByDefault={ isBorderRadiusShownByDefault }
				>
					<BorderRadiusControl
						values={ borderRadiusValues }
						onChange={ ( newValue ) => {
							setBorderRadius( newValue || undefined );
						} }
						readonlyCorners={ {
							topLeft:
								'readonly' === value.borderTopLeftRadius?.type,
							topRight:
								'readonly' === value.borderTopRightRadius?.type,
							bottomRight:
								'readonly' ===
								value.borderBottomRightRadius?.type,
							bottomLeft:
								'readonly' ===
								value.borderBottomRightRadius?.type,
						} }
					/>
				</ToolsPanelItem>
			) }
			{ hasShadowControl && (
				<ToolsPanelItem
					label={ _x( 'Shadow', 'text', 'nelio-ab-testing' ) }
					hasValue={ () => hasUserValue( value.boxShadow ) }
					onDeselect={ () => onChange( { boxShadow: undefined } ) }
					isShownByDefault={ hasInheritedValue( value.boxShadow ) }
				>
					{ hasBorderControl ? (
						<BaseControl.VisualLabel as="legend">
							{ _x( 'Shadow', 'text', 'nelio-ab-testing' ) }
						</BaseControl.VisualLabel>
					) : null }

					<ItemGroup isBordered isSeparated>
						<ShadowPopover
							value={ getUserValue( value.boxShadow ) }
							inheritedValue={ getInheritedValue(
								value.boxShadow
							) }
							disabled={ 'readonly' === value.boxShadow?.type }
							onChange={ ( boxShadow ) =>
								onChange( { boxShadow } )
							}
							settings={ settings }
						/>
					</ItemGroup>
				</ToolsPanelItem>
			) }
		</ToolsPanel>
	);
}

// =======
// HELPERS
// =======

const useBorder = (
	props: ToolsPanelProps[ 'value' ]
): BorderValue[ 'border' ] => {
	const radius = {
		topLeft: getPreviewValue( props.borderTopLeftRadius ),
		topRight: getPreviewValue( props.borderTopRightRadius ),
		bottomRight: getPreviewValue( props.borderBottomRightRadius ),
		bottomLeft: getPreviewValue( props.borderBottomLeftRadius ),
	};

	if ( hasUnifiedBorderSettings( props ) ) {
		return {
			color: getPreviewValue( props.borderTopColor ),
			style: getPreviewValue( props.borderTopStyle ),
			width: getPreviewValue( props.borderTopWidth ),
			radius,
		};
	}

	return {
		top: {
			color: getPreviewValue( props.borderTopColor ),
			style: getPreviewValue( props.borderTopStyle ),
			width: getPreviewValue( props.borderTopWidth ),
		},
		right: {
			color: getPreviewValue( props.borderRightColor ),
			style: getPreviewValue( props.borderRightStyle ),
			width: getPreviewValue( props.borderRightWidth ),
		},
		bottom: {
			color: getPreviewValue( props.borderBottomColor ),
			style: getPreviewValue( props.borderBottomStyle ),
			width: getPreviewValue( props.borderBottomWidth ),
		},
		left: {
			color: getPreviewValue( props.borderLeftColor ),
			style: getPreviewValue( props.borderLeftStyle ),
			width: getPreviewValue( props.borderLeftWidth ),
		},
		radius,
	};
};

const useBorderReadonlies = ( props: ToolsPanelProps[ 'value' ] ) => ( {
	top: {
		color: 'readonly' === props.borderTopColor?.type,
		style: 'readonly' === props.borderTopStyle?.type,
		width: 'readonly' === props.borderTopWidth?.type,
	},
	right: {
		color: 'readonly' === props.borderRightColor?.type,
		style: 'readonly' === props.borderRightStyle?.type,
		width: 'readonly' === props.borderRightWidth?.type,
	},
	bottom: {
		color: 'readonly' === props.borderBottomColor?.type,
		style: 'readonly' === props.borderBottomStyle?.type,
		width: 'readonly' === props.borderBottomWidth?.type,
	},
	left: {
		color: 'readonly' === props.borderLeftColor?.type,
		style: 'readonly' === props.borderLeftStyle?.type,
		width: 'readonly' === props.borderLeftWidth?.type,
	},
} );

function hasUnifiedBorderSettings(
	props: ToolsPanelProps[ 'value' ]
): boolean {
	return (
		getPreviewValue( props.borderTopColor ) ===
			getPreviewValue( props.borderRightColor ) &&
		getPreviewValue( props.borderTopColor ) ===
			getPreviewValue( props.borderBottomColor ) &&
		getPreviewValue( props.borderTopColor ) ===
			getPreviewValue( props.borderLeftColor ) &&
		getPreviewValue( props.borderTopStyle ) ===
			getPreviewValue( props.borderRightStyle ) &&
		getPreviewValue( props.borderTopStyle ) ===
			getPreviewValue( props.borderBottomStyle ) &&
		getPreviewValue( props.borderTopStyle ) ===
			getPreviewValue( props.borderLeftStyle ) &&
		getPreviewValue( props.borderTopWidth ) ===
			getPreviewValue( props.borderRightWidth ) &&
		getPreviewValue( props.borderTopWidth ) ===
			getPreviewValue( props.borderBottomWidth ) &&
		getPreviewValue( props.borderTopWidth ) ===
			getPreviewValue( props.borderLeftWidth )
	);
}

function getColorFromPreset( value: Maybe< string > ): Maybe< string > {
	const settings = getEditorSettings();
	const color = settings.colors.find(
		( { slug } ) => value === `var:preset|color|${ slug }`
	);
	return color?.color ?? value;
}
